import {
  LitElement,
  html,
  classMap,
  css,
  unsafeHTML,
  t,
  paths,
} from '@hh/global.js'

class HHWistiaVideoWrapper extends LitElement {
  static properties = {
    videoId: {
      type: String,
      reflect: true,
      attribute: 'video-id',
    },
    mobileVideoId: {
      type: String,
      reflect: true,
      attribute: 'mobile-video-id',
    },
    paused: {
      type: Boolean,
      reflect: true,
    },
    video: {
      type: Object,
    },
    mobileVideo: {
      type: Object,
    },
    isHero: {
      type: Boolean,
    },
    isCollection: {
      type: Boolean,
      reflect: true,
      attribute: 'is-collection',
    },
    isHeroCarousel: {
      type: Boolean,
      reflect: true,
      attribute: 'is-hero-carousel',
    },
    onlyMobile: {
      type: Boolean,
      reflect: true,
      attribute: 'only-mobile',
    },
    onlyDesktop: {
      type: Boolean,
      reflect: true,
      attribute: 'only-desktop',
    },
    wistiaControls: {
      type: Boolean,
      reflect: true,
      attribute: 'wistia-controls',
    },
    forceVideoDimensions: {
      type: Boolean,
      attribute: 'force-video-dimensions',
    },
    muted: {
      type: Boolean,
      attribute: 'muted',
      default: false
    },
    autoplay: {
      type: Boolean,
      attribute: 'autoplay',
      default: true
    },
    isPdp: {
      type: Boolean,
      attribute: 'is-pdp',
      default: false
    },
    isHovered: { type: Boolean }
  }


  constructor() {
    super()
    window._wq = window._wq || []
    this.isMobile = matchMedia('(max-width: 749px)').matches
    this.isMotionReduced = matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
    this.hasMobileVideo = false
    this.autoplay = String(this.autoplay) === "true";
    this.isPdp = String(this.isPdp) === "true";
    this.isHovered = false

    if (!window._nativeFetch) {
      window._nativeFetch = window.fetch
    }
  }

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback()

    this.paused = true
    this.wistiaOptions = {
      videoFoam: true,
      fitStrategy: 'cover',
      endVideoBehavior: 'loop',
      controlsVisibleOnLoad: this.wistiaControls,
      playSuspendedOffScreen: false,
      playPauseNotifier: false,
      autoPlay: this.autoplay
    }

    window.addEventListener('resize', this.onResize.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('resize', this.onResize)
  }

  firstUpdated() {
    this.containerHeight = this.getBoundingClientRect().height
    const self = this

      const oldFetch = window.fetch
      window.fetch = window._nativeFetch

    if (this.isMobile && this.mobileVideoId) {
      this.hasMobileVideo = true
      _wq.push({
        id: this.mobileVideoId,
        options: this.wistiaOptions,
        onReady: (video) => {
          this.mobileVideo = video
          video.bind('play', () => {
            this.paused = false
          })
          video.bind('pause', () => {
            this.paused = true
          })
        },
      })
    }

    _wq.push({
      id: this.videoId,
      options: this.wistiaOptions,
      onReady: (video) => {
        this.video = video
        video.bind('play', () => {
          this.paused = false
        })
        video.bind('pause', () => {
          this.paused = true
        })
      },
    })

    window.fetch = oldFetch
  }

  onResize() {
    this.containerHeight = this.getBoundingClientRect().height
    this.isMobile = matchMedia('(max-width: 749px)').matches
  }

  isVisible() {
    const rect = this.getBoundingClientRect()
    const windowHeight = window.innerHeight || document.documentElement.clientHeight
    const windowTop = window.scrollY || window.pageYOffset

    const top = rect.top + windowTop
    const bottom = rect.bottom + windowTop

    return bottom > windowTop && top < windowTop + windowHeight
  }

  onToggleClick(event) {
    event.stopPropagation()
    event.preventDefault()
    if (this.paused) {
      this.play()
    } else {
      this.pause()
    }
  }

  async play() {
    const isMobile = this.hasMobileVideo && this.isMobile
    const video = isMobile ? this.mobileVideo : this.video
    video.play()
    this.paused = false
  }

  pause() {
    if (this.hasMobileVideo && this.isMobile) {
      this.mobileVideo?.pause()
    } else {
      this.video?.pause()
    }
    this.paused = true
  }

  render() {
    return html`
      <div
        class="wrapper"
        @mouseenter=${() => { this.isHovered = true }}
        @mouseleave=${() => { this.isHovered = false }}
      >
        <slot name="video"></slot>
        <slot name="mobile-video"></slot>
        <button
          class="${classMap({
            toggle: true,
            ready: this.videoReady,
            mobileReady: true,
            isCollection: this.isCollection,
            isHeroCarousel: this.isHeroCarousel,
            isHero: this.isHero,
            autoplay: this.autoplay,
            'use-wistia-controls': this.wistiaControls,
            'isPdp': this.isPdp,
            'show-play': this.paused,
            'hide-toggle': this.isPdp && !this.paused && !this.isHovered
          })}"
          @click=${this.onToggleClick}
          aria-label=${this.paused
            ? t('accessibility.play')
            : t('accessibility.pause')}
        >
          ${this.paused
            ? this.isPdp
              ? unsafeHTML(paths.pdpPlay)
              : unsafeHTML(paths.play)
            : unsafeHTML(paths.pause)}
        </button>
      </div>
    `
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    :host {
      display: block;
      height: 100%;
      background: var(--hh-color-foreground);
    }

    :host([only-desktop]) {
      display: none;
    }

    @media (min-width: 750px) {
      :host([only-desktop]) {
        display: block;
      }

      :host([only-mobile]) {
        display: none;
      }
    }

    .wrapper {
      height: 100%;
      position: relative;
    }

    .toggle {
      display: none;
      position: absolute;
      bottom: 20px;
      right: 16px;
      width: 2.5000rem;
      height: 2.5000rem;
      border-radius: 50%;
      border: none;
      color: var(--hh-color-background);
      background-color: var(--hh-color-day-extra-light-opacity);
      cursor: pointer;
      padding: 0;
      fill: currentColor;
      align-items: center;
      justify-content: center;
      z-index: 11;
    }

    .toggle.isPdp {
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%) scale(1);
      background-color: var(--hh-color-background);
      color: var(--hh-color-foreground);
      height: 3.8750rem;
      width: 3.8750rem;
    }

    .toggle.isPdp.show-play svg {
      width: 20px;
      height: 20px;
    }

    .toggle.isPdp:hover {
      transform: translate(-50%, -50%) scale(1.1);
    }

    .toggle.isCollection {
      bottom: 0.6250rem;
      right: 0.6250rem;
    }

    .toggle.isHeroCarousel {
      bottom: 2.1875rem !important;
    }

    .toggle.mobileReady {
      display: flex;
    }

    .toggle.isHero {
      display: flex;
    }

    @media (min-width: 750px) {
      .toggle {
        bottom: var(--hh-toggle-bottom-offset, 3.1250rem);
        right: 3.1250rem;
      }

      .toggle.isCollection {
        bottom: 0.6250rem;
        right: 0.6250rem;
      }

      .toggle.ready {
        display: flex;
      }

      .toggle.isHero {
        display: flex;
      }
    }

    .toggle.use-wistia-controls {
      display: none;
    }

    .toggle svg {
      fill: transparent;
      transition: fill var(--hh-duration-default) ease-in-out;
    }

    ::slotted(.wistia_embed) {
      pointer-events: none;
    }

    .toggle.hide-toggle { 
      display: none !important;   
    }

      @media (hover: hover) {
        .toggle:is(:focus-visible, :hover) svg {
          fill: currentColor;
        }
      }
  `
}

customElements.define('hh-wistia-video-wrapper', HHWistiaVideoWrapper)
